<template>
  <div id="app" class="min-h-screen" style="background-color: var(--monaco-bg-primary); color: var(--monaco-text-primary);">
    <!-- White Container with Dark Edges -->
    <div class="monaco-white-container">
      <div class="monaco-dark-edge"></div>
      <div class="monaco-white-content">
        <!-- Navigation -->
        <nav class="monaco-card sticky top-0 z-50" style="margin: -24px -24px 24px -24px; border-radius: 8px 8px 0 0; border-left: none; border-right: none; border-top: none; box-shadow: 0 2px 8px var(--monaco-shadow);">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <router-link to="/" class="nav-logo">
                <div class="monaco-logo">
                  <img 
                    src="/logo.png" 
                    alt="Vue3 Vite Logo" 
                    @error="handleImageError"
                  />
                </div>
                <span class="monaco-logo-text">
                  Vue3 Vite
                </span>
              </router-link>
            </div>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:block">
            <div class="ml-10 flex items-baseline space-x-4">
              <router-link
                v-for="item in navigation"
                :key="item.name"
                :to="item.to"
                class="px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                :class="[
                  $route.path === item.to
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                ]"
              >
                {{ item.name }}
              </router-link>
            </div>
          </div>

          <!-- Mobile menu button -->
          <div class="md:hidden">
            <button
              @click="mobileMenuOpen = !mobileMenuOpen"
              class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Mobile menu -->
        <div v-if="mobileMenuOpen" class="md:hidden">
          <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <router-link
              v-for="item in navigation"
              :key="item.name"
              :to="item.to"
              class="block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200"
              :class="[
                $route.path === item.to
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="mobileMenuOpen = false"
            >
              {{ item.name }}
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="flex-1">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Footer -->
    <footer class="monaco-card" style="margin: 0; border-radius: 0; border-left: none; border-right: none; border-top: 1px solid var(--monaco-border);">
      <div class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Company Info -->
          <div>
            <div class="flex items-center space-x-3 mb-4">
              <router-link 
                to="/" 
                class="footer-logo"
              >
                <div class="monaco-logo">
                  <img 
                    src="/logo.png" 
                    alt="Vue3 Vite Logo" 
                    @error="handleImageError"
                  />
                </div>
                <span class="monaco-logo-text">
                  Vue3 Vite App
                </span>
              </router-link>
            </div>
            <p class="text-gray-400 text-sm">
              Modern Vue 3 + Vite application with the latest features and best practices.
            </p>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-lg font-semibold mb-4" style="color: var(--monaco-text-primary);">Quick Links</h3>
            <ul class="space-y-2">
              <li v-for="link in footerLinks" :key="link.name">
                <router-link
                  :to="link.to"
                  class="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                >
                  {{ link.name }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Tech Stack -->
          <div>
            <h3 class="text-lg font-semibold mb-4" style="color: var(--monaco-text-primary);">Tech Stack</h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="tech in techStack"
                :key="tech"
                class="monaco-badge"
                :class="{
                  'success': tech === 'Vue 3' || tech === 'Vite',
                  'warning': tech === 'Pinia' || tech === 'Vue Router'
                }"
              >
                {{ tech }}
              </span>
            </div>
          </div>
        </div>

        <div class="mt-8 pt-8 border-t border-gray-800 text-center">
          <div class="monaco-status-bar">
            <span style="color: var(--monaco-text-secondary);">
              &copy; {{ new Date().getFullYear() }} Vue3 Vite App. Built with Monaco-style interface.
            </span>
            <span style="color: var(--monaco-text-secondary);">
              Powered by Vue 3 + Vite + Monaco Theme
            </span>
          </div>
        </div>
      </div>
    </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const mobileMenuOpen = ref(false)

const handleImageError = (event) => {
  // Fallback to neon logo if image fails to load
  event.target.style.display = 'none'
  const parent = event.target.parentElement
  const fallbackLogo = document.createElement('div')
  fallbackLogo.className = 'monaco-logo-fallback'
  fallbackLogo.innerHTML = 'V3'
  parent.insertBefore(fallbackLogo, event.target)
}

const navigation = [
  { name: 'Home', to: '/' },
  { name: 'Dashboard', to: '/dashboard' },
  { name: 'Media', to: '/media' },
  { name: 'AI Prompting', to: '/prompting' },
  { name: 'About', to: '/about' }
]

const footerLinks = [
  { name: 'Home', to: '/' },
  { name: 'Dashboard', to: '/dashboard' },
  { name: 'Media', to: '/media' },
  { name: 'AI Prompting', to: '/prompting' },
  { name: 'About', to: '/about' }
]

const techStack = [
  'Vue 3',
  'Vite',
  'Pinia',
  'Vue Router',
  'Tailwind CSS',
  'PostCSS'
]
</script>

<style scoped>
/* Page transition animations */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.page-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
